/**
 * Schedule GRN Tab — shows GRNs attributed to this specific schedule line,
 * with a running "fill" bar per GRN so the reader can see the schedule
 * filling up delivery-by-delivery. Mirrors the Delivery modal's Schedule
 * Allocation tab visual.
 */
import { useEffect, useState, useMemo } from 'react';
import { Loader2, Package, AlertCircle, Info } from 'lucide-react';
import { purchasingAPI } from '../../lib/api';
import type { GoodsReceiptNote } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface ScheduleGRNTabProps {
  poLineId: number;
  poNumber: string;
  lineNumber: number;
  scheduleId?: number;
  scheduleQty?: number;
}

interface GRNSummaryResponse {
  total_grns: number;
  total_received: string;
  total_accepted: string;
  total_rejected: string;
  pending_inspection: number;
  grns: GoodsReceiptNote[];
}

export default function ScheduleGRNTab({ poLineId, poNumber, lineNumber, scheduleId, scheduleQty }: ScheduleGRNTabProps) {
  const { formatDate: contextFormatDate, formatQuantity } = useFormatters();
  const [summary, setSummary] = useState<GRNSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await purchasingAPI.getLineGRNSummary(poLineId);
        if (!cancelled) setSummary(res.data);
      } catch (err: unknown) {
        console.error('Failed to fetch GRNs for schedule:', err);
        if (!cancelled) {
          const axiosError = err as { response?: { data?: { detail?: string } } };
          setError(axiosError.response?.data?.detail || 'Failed to load GRNs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [poLineId]);

  const formatDate = (d: string | undefined) => (d ? contextFormatDate(d) : 'N/A');

  // Build the row list: GRNs with any allocation to this schedule, sorted
  // chronologically, with running totals so the fill bar walks the
  // schedule's demand down to 0.
  const rows = useMemo(() => {
    if (!summary || !scheduleId) return [] as Array<{ grn: GoodsReceiptNote; allocated: number; priorBefore: number; runningAfter: number; balance: number }>;
    const withAlloc = summary.grns
      .map(g => {
        const a = (g.allocations ?? []).find(x => x.po_line_schedule === scheduleId);
        let allocated = 0;
        if (a) allocated = parseFloat(a.allocated_qty);
        else if (g.po_line_schedule === scheduleId) allocated = parseFloat(g.quantity_received || '0');
        return { grn: g, allocated };
      })
      .filter(r => r.allocated > 0);
    withAlloc.sort((a, b) => (a.grn.receipt_date || '').localeCompare(b.grn.receipt_date || ''));
    const total = scheduleQty ?? 0;
    let running = 0;
    return withAlloc.map(r => {
      const priorBefore = running;
      running += r.allocated;
      const balance = Math.max(0, total - running);
      return { ...r, priorBefore, runningAfter: running, balance };
    });
  }, [summary, scheduleId, scheduleQty]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>
          {scheduleId
            ? <>GRNs attributed to this schedule line on <strong>{poNumber} L{lineNumber}</strong>. The bar grows with each delivery until the schedule is filled.</>
            : <>Showing all GRNs for parent PO line <strong>{poNumber} L{lineNumber}</strong>.</>}
        </span>
      </div>

      {scheduleId && scheduleQty !== undefined && (
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-gray-400 rounded-sm" />
            Filled by earlier GRNs
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-blue-500 rounded-sm" />
            This GRN
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-amber-200 rounded-sm border border-amber-300" />
            Balance remaining
          </div>
        </div>
      )}

      {!summary || rows.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">No GRNs attributed to this schedule line</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">GRN Number</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">Receipt Date</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">Qty Received</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">Fill Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map(({ grn, allocated, priorBefore, runningAfter, balance }, idx) => {
                const total = scheduleQty ?? 0;
                const priorPct = total > 0 ? (priorBefore / total) * 100 : 0;
                const thisPct = total > 0 ? (allocated / total) * 100 : 0;
                const balPct = Math.max(0, 100 - priorPct - thisPct);
                return (
                  <tr key={grn.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}>
                    <td className="px-4 py-2 text-sm font-medium text-primary">{grn.grn_number}</td>
                    <td className="px-4 py-2 text-sm">{formatDate(grn.receipt_date)}</td>
                    <td className="px-4 py-2 text-sm text-right font-medium">{allocated.toFixed(3)}</td>
                    <td className="px-4 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-40 h-3 bg-gray-50 rounded border border-gray-200 overflow-hidden flex"
                          title={`Before: ${priorBefore.toFixed(3)} · +${allocated.toFixed(3)} this GRN · After: ${runningAfter.toFixed(3)} of ${total.toFixed(3)} (${balance.toFixed(3)} remaining)`}
                        >
                          {priorPct > 0 && <div className="bg-gray-400 h-full" style={{ width: `${priorPct}%` }} />}
                          {thisPct > 0 && <div className="bg-blue-500 h-full" style={{ width: `${thisPct}%` }} />}
                          {balPct > 0 && <div className="bg-amber-200 h-full" style={{ width: `${balPct}%` }} />}
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatQuantity(runningAfter)} / {formatQuantity(total)}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
